;(function (angular, _, moment) {
  'use strict'

  class QuoteService {
    $q
    $http
    $state
    $timeout
    $rootScope
    exportService
    preferencesService
    invoiceManager
    quoteManager
    snackService
    statusService
    tokenSwitcherService
    utilService
    sessionService
    paymentService
    DisplayService
    urls
    quoteInvoiceDueDateCompany
    $uibModal

    constructor (
      // Angular
      $http, $q, $state, $timeout, $rootScope,
      // Libs
      $uibModal,
      // Managers
      InvoiceManager, QuoteManager,
      // Services
      ExportService, PaymentService, PreferencesService, SnackService, StatusService, TokenSwitcherService, UtilService, SessionService, DisplayService,
      // Const
      urls, quoteInvoiceDueDateCompany
      // Resolved
    ) {
      this.$http = $http
      this.$q = $q
      this.$state = $state
      this.$timeout = $timeout
      this.$rootScope = $rootScope
      this.$uibModal = $uibModal
      this.invoiceManager = InvoiceManager
      this.quoteManager = QuoteManager
      this.exportService = ExportService
      this.paymentService = PaymentService
      this.preferencesService = PreferencesService
      this.snackService = SnackService
      this.statusService = StatusService
      this.tokenSwitcherService = TokenSwitcherService
      this.utilService = UtilService
      this.urls = urls
      this.quoteInvoiceDueDateCompany = quoteInvoiceDueDateCompany
      this.company = SessionService.getCompany()
      this.displayService = DisplayService
    }

    get manager () {
      return this.quoteManager
    }

    createQuoteFromPreferences () {
      return this.$q.all([
        this.utilService.getLegalNotices(),
        this.preferencesService.getQuotePrefs()
      ]).then(([legalNotices, quotePrefs]) => {
        return this.quoteManager.create({
          status: 'draft',
          history: [],
          conditions: _.get(quotePrefs, 'defaultConditions', '').replace(/\n/g, '<br>'),
          thanks: _.get(quotePrefs, 'defaultThanks', '').replace(/\n/g, '<br>'),
          legalNotices: legalNotices,
          depositPrice: 0,
          customFields: {}
        })
      })
    }

    setStatus (quote, status) {
      switch (status) {
        case 'validated':
          return this._validate(quote)
        case 'sent':
          return this._sendQuote(quote)
        case 'signed':
          return this._markQuote(quote, status)
        case 'refused':
          return this._markQuote(quote, status)
        case 'invoiced':
          return this._toInvoice(quote)
        case 'deposit':
          return this._toDeposit(quote)
        default:
          return this._switchToStatus(quote, status)
      }
    }

    changeDueDate (quote) {
      return this.preferencesService.getPrefsFor('quote').then((prefs) => {
        if (quote.client && quote.client.paymentDelay) {
          quote.dueDate = moment(quote.date).add(quote.client.paymentDelay, 'days').toDate()
        } else {
          quote.dueDate = moment(quote.date).add(prefs.quoteDelay, 'days').toDate()
        }
        return quote
      })
    }

    /**
     * Return available status for given quote
     * @param quote
     * @return {*}
     */
    getAllowedStatus (quote) {
      return this.statusService.getAllowedStatus('quote', quote.status)
    }

    asPdf (quoteID) {
      return this.exportService.quoteAsPdf(quoteID)
    }

    asPdfWithInterventions (quoteId) {
      return this.exportService.quoteAsPdfWithInterventions(quoteId)
    }

    asCsv (quoteID) {
      return this.exportService.quoteAsCsv(quoteID)
    }

    asSpreadsheet (quoteID) {
      return this.exportService.quoteAsSpreadsheet(quoteID)
    }

    duplicate (quote) {
      return this.preferencesService.getPrefsFor('quote').then((prefs) => {
        const q = _.cloneDeep(quote)
        q.invoiceDeposit = []
        if (q.numberFormat) delete q.numberFormat
        const newQuote = this.quoteManager.create(q.duplicate())
        if (!quote.dueDate) {
          if (quote.client && quote.client.paymentDelay) {
            newQuote.dueDate = moment(new Date()).add(quote.client.paymentDelay, 'days').toDate()
          } else {
            newQuote.dueDate = moment(new Date()).add(prefs.quoteDelay, 'days').toDate()
          }
        }
        this.$state.go('quote.new', { initParams: newQuote })
        this.snackService.snack({
          text: 'Devis dupliqué !',
          delay: 5000
        })
      })
    }

    getDueDate (quote, date = new Date()) {
      const _date = moment(date)
      return this.preferencesService.getPrefsFor('quote').then((prefs) => {
        if (quote?.client?.paymentDelay) {
          if (quote.client.paymentDelayEndOfMonth) {
            return moment(_date).add(quote.client.paymentDelay, 'days').endOf('month').toDate()
          }
          return moment(_date).add(quote.client.paymentDelay, 'days').toDate()
        }
        if (prefs.quoteDelay !== 0) {
          return moment(_date).add(prefs.quoteDelay, 'days').toDate()
        }
        return moment(_date).toDate()
      })
    }

    createIntervention (quote, templateId) {
      return this.exportService.quoteAsPdf(quote._id, { sync: true, templateId }).then((file) => {
        const files = [file.data[0]._id]
        return this.$http.post(`${this.urls.api}/solica/inmails`, {
          billingEntity: quote.client?._id || quote.client,
          shippingEntity: quote.delivery?._id || quote.delivery,
          title: quote.title,
          description: quote.description,
          files: files,
          linkedData: {
            kilo: {
              quotes: [quote._id]
            }
          }
        }).then(({ data }) => {
          this.snackService.snack({
            text: 'Devis exporté',
            delay: 5000
          })
          return this.tokenSwitcherService.switchApp('soli', true).then((token) => {
            window.open(`${this.urls.soli}/inbox/${data._id}#${token}`, '_blank')
          }).catch(() => {})
        }).catch(() => {
          this.snackService.snack({
            text: `Une erreur est survenue lors de l'export`,
            delay: 5000
          })
        })
      })
    }

    // PRIVATES

    _validate (quote) {
      return this.$uibModal.open({
        controllerAs: 'valid',
        bindToController: true,
        resolve: {},
        templateUrl: '/app/modules/quote/show/templates/validate-quote.template.html',
        controller: 'ValidateQuoteController'
      }).result.then((result) => {
        if (result) {
          return this._switchToStatus(quote, 'validated').then((q) => {
            this.snackService.snack({
              text: 'Devis validé !',
              delay: 5000
            })
            return q
          })
        }
      })
    }

    _sendQuote (quote) {
      return this.$uibModal.open({
        controllerAs: 'send',
        bindToController: true,
        resolve: {
          obj: function () {
            return quote
          },
          type: function () { return 'quote' },
          asInter: !!_.get(quote, 'linkedData.solica.interventions', []).length
        },
        templateUrl: '/app/modules/invoice/show/templates/send-invoice.template.html',
        controller: 'SendInvoiceController'
      }).result.then((result) => {
        const promises = []
        if (result.mail) {
          const recipients = result.emails.filter(function (email) {
            return email.added
          }).map(function (e) { return { Email: e.email } })
          let url = this.urls.backend
          if (!result.withInter) {
            url += '/services/pdf/quote/emails/'
          } else {
            url += '/services/pdf/quote-with-interventions/emails/'
          }
          if (result.mailTemplate) {
            url += result.mailTemplate._id
          }
          const toSend = {
            quotes: [quote._id],
            recipients: recipients,
            email: result.customMailTemplate,
            subject: result.subject
          }
          if (result.model && result.model.length > 0) {
            toSend.model = result.model
            toSend.smtp = true
          }
          promises.push(this.$http.post(url, toSend))
        }
        if (result.print) {
          // TODO: print
        }
        if (result.status) {
          promises.push(this._switchToStatus(quote, 'sent'))
        }
        return this.$q.all(promises).then((results) => {
          this.snackService.snack({
            text: 'Devis envoyé !',
            delay: 5000
          })
          if (!result.status) {
            return quote
          }
          // Get updated invoice form promises
          return results[results.length - 1]
        })
      })
    }

    _markQuote (quote, status) {
      return this.$uibModal.open({
        controllerAs: 'mark',
        bindToController: true,
        resolve: {
          status: function () {
            return status
          }
        },
        templateUrl: '/app/modules/quote/show/templates/mark-quote.template.html',
        controller: 'MarkQuoteController'
      }).result.then((result) => {
        if (result) {
          let promise
          if (status === 'signed') {
            quote.signDate = new Date(result)
            quote.status = 'signed'
            promise = quote.save()
          } else {
            promise = this._switchToStatus(quote, status)
          }
          return promise.then((r) => {
            this.snackService.snack({
              text: 'Devis marqué comme ' + (status === 'refused' ? 'refusé' : 'signé') + ' !',
              delay: 5000
            })
            return r
          })
        }
      })
    }

    _toDeposit (quote) {
      let deposit = _.get(quote, 'invoiceDeposit', [])
      deposit = deposit.filter(el => !el.cancelByUser)
      if (deposit.length > 0) {
        const content = 'Une facture d\'acompte existe déja,<br><br>Êtes-vous sûr de vouloir continuer ?'
        return this.displayService.displayConfirm('Acompte', content).then(() => {
          return this._setDeposit(quote)
        })
      } else {
        return this._setDeposit(quote)
      }
    }

    _setDeposit (quote) {
      const deposit = _.get(quote, 'invoiceDeposit', [])
      quote.invoiceDeposit = quote.invoiceDeposit.filter(e => !e.cancelByUser)
      return this.$uibModal.open({
        controllerAs: 'toDeposit',
        bindToController: true,
        resolve: {
          invoice: {
            percent: quote.depositPercent,
            amount: false,
            price: quote.depositPrice ? quote.depositPrice : 0,
            total: 8000,
            label: quote.title,
            number: this.utilService.getInvoiceNumber(quote, 'quote', quote.numberFormat)
          },
          quote
        },
        templateUrl: '/app/modules/quote/show/templates/to-deposit.template.html',
        controller: 'ToDepositController'
      }).result.then((result) => {
        if (result) {
          return this.preferencesService.getPrefsFor('invoice').then((prefs) => {
            let toCopy = angular.copy(quote)
            toCopy.lines = result.lines
            toCopy.title = result.label
            toCopy.rubrics = []
            const invoice = this.invoiceManager.create(toCopy.clone())
            invoice.conditions = _.get(prefs, 'defaultConditions', '').replace(/\n/g, '<br>')
            invoice.thanks = _.get(prefs, 'defaultThanks', '').replace(/\n/g, '<br>')
            invoice.status = 'draft'
            invoice.history = []
            invoice.type = 'deposit'
            delete invoice.number
            delete invoice.date
            delete invoice.dueDate
            return invoice.save().then(() => {
              this.snackService.snack({
                text: 'facture d\'acompte créée !',
                delay: 5000
              })
              quote.invoiceDeposit = deposit
              quote.invoiceDeposit.push(invoice._id)
              return quote.save().then(() => {
                // Ensure navigation to invoice
                this.$timeout(() => {
                  this.$state.go('invoice.show', { id: invoice._id })
                })
                return quote
              })
            })
          })
        }
      })
    }

    _toInvoice (quote) {
      let payed = true
      let i = 0
      let j
      if (quote.invoiceDeposit && quote.invoiceDeposit.length > 0) {
        quote.invoiceDeposit.forEach((el) => {
          if (el.status !== 'paid') {
            payed = false
            i += 1
            j = el
          }
        })
      }
      if (!payed) {
        const content = `${i > 1 ? 'Des factures d\'acompte' : 'Une facture d\'acompte'} est sans paiement<br><br>Désirez-vous être redirigé vers l'acompte ?`
        return this.displayService.displaygoTo('Facturation', content).then(() => {
          return this.$state.go('invoice.show', { id: j._id })
        })
      } else {
        return this.$uibModal.open({
          controllerAs: 'toInv',
          bindToController: true,
          resolve: {},
          templateUrl: '/app/modules/quote/show/templates/to-invoice.template.html',
          controller: 'ToInvoiceController'
        }).result.then((result) => {
          if (result) {
            return this._switchToStatus(quote, 'invoiced').then((result) => {
              return this._saveToInvoice(result)
            })
          }
        })
      }
    }

    _saveToInvoice (quote) {
      this.$rootScope.loadingPage = true
      return this.preferencesService.getPrefsFor('invoice').then((prefs) => {
        const invoice = this.invoiceManager.create(quote.clone())
        invoice.conditions = _.get(prefs, 'defaultConditions', '').replace(/\n/g, '<br>')
        invoice.thanks = _.get(prefs, 'defaultThanks', '').replace(/\n/g, '<br>')
        invoice.status = 'draft'
        invoice.history = []
        delete invoice.number
        delete invoice.date
        delete invoice.dueDate
        if (quote.invoiceDeposit && quote.invoiceDeposit.length > 0) {
          if (!invoice.payments) {
            invoice.payments = []
          }
          quote.invoiceDeposit.forEach((el) => {
            if (!el.cancelByUser) {
              const ref = this._getInvoiceNumber(el)
              let p = angular.copy(el.payments)
              p = p.map((d) => {
                return {
                  ...d,
                  transactionId: ref,
                  invoiceRef: el._id
                }
              })
              if (el.status === 'paid') {
                invoice.payments.push(...p)
              } else {
                invoice.payments.push({
                  amount: {
                    amount: el.computed.fullTotal,
                    currency: 'EUR'
                  },
                  invoiceRef: el._id,
                  date: new Date(),
                  type: 'not-documented',
                  transactionId: this._getInvoiceNumber(el)
                })
              }
            }
          })
        }
        return this.$http.post(`${this.urls.api}/kilo/invoices`, invoice).then((data) => {
          this.snackService.snack({
            text: 'Devis passé en facturation !',
            delay: 5000
          })
          this.$rootScope.loadingPage = false
          quote.invoice = data.data._id
          return quote.save().then(() => {
            // Ensure navigation to invoice
            this.$timeout(() => {
              this.$state.go('invoice.show', { id: data.data._id })
            })
            return quote
          })
        })
      })
    }

    _getInvoiceNumber (invoice) {
      if (invoice.status === 'draft') return 'Brouillon'
      return this.utilService.getInvoiceNumber(invoice, 'invoice', invoice.numberFormat)
    }

    /**
     * Update quote status
     * @param quote {Quote}
     * @param status {'validated' | 'sent' | 'signed' | 'refused' | 'invoiced'}
     * @return {Promise<*>|void|*}
     * @private
     */
    _switchToStatus (quote, status) {
      if (status === 'validated') {
        return this.preferencesService.getQuotePrefs().then((quotePrefs) => {
          quote.status = 'validated'
          quote.periodicity = quotePrefs.periodicity
          quote.resetDate = quotePrefs.resetDate
          quote.numberFormat = quotePrefs.numberFormat
          if (!quote.date || this.quoteInvoiceDueDateCompany.filter(e => e.value === this.company).length > 0) {
            quote.date = new Date()
          }
          if (!quote.dueDate || this.quoteInvoiceDueDateCompany.filter(e => e.value === this.company).length > 0) {
            return this.changeDueDate(quote).then((result) => {
              return result.validate()
            })
          } else {
            return quote.validate()
          }
        })
      }
      return quote.setStatus(status)
    }

    searchToSpreadsheet (conditions, total) {
      return this.exportService.searchToSpreadsheet(conditions, total, 'quote')
    }
  }

  QuoteService.$inject = [
    // Angular
    '$http', '$q', '$state', '$timeout', '$rootScope',
    // Libs
    '$uibModal',
    // Managers
    'InvoiceManager', 'QuoteManager',
    // Services
    'ExportService', 'PaymentService', 'PreferencesService', 'SnackService', 'StatusService', 'TokenSwitcherService', 'UtilService', 'SessionService', 'DisplayService',
    // Const
    'urls', 'quoteInvoiceDueDateCompany'
    // Resolved
  ]

  angular
    .module('kilo.common.services')
    .service('QuoteService', QuoteService)
})(window.angular, window._, window.moment)
